import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../api';
import SimplifiedTranscriptForm from './SimplifiedTranscriptForm';
import AnalysisProgress from './AnalysisProgress';
import { useSSEAnalysis } from '../hooks/useSSEAnalysis';

/**
 * AddSessionDrawer - Slide-out drawer for creating new sessions
 *
 * Combines transcript form submission with real-time SSE analysis progress.
 * Notifies parent when session is created and analyzed.
 */
const AddSessionDrawer = ({
  isOpen,
  onClose,
  onSessionCreated,
  students = [],
  loadingStudents = false,
  selectedStudentUuid = null
}) => {
  const [step, setStep] = useState('form'); // 'form' | 'analyzing' | 'complete'
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [createdTranscript, setCreatedTranscript] = useState(null);

  const {
    startAnalysis,
    reset: resetAnalysis,
    progress,
    summary,
    error: analysisError,
    isAnalyzing,
    phases
  } = useSSEAnalysis();

  // When analysis completes successfully
  useEffect(() => {
    if ((progress?.stage === 'complete' || progress?.stage === 'partial') && summary && createdTranscript) {
      setStep('complete');

      // Notify parent with full session data
      if (onSessionCreated) {
        onSessionCreated({
          ...createdTranscript,
          summary,
          analyzed: true
        });
      }
    }
  }, [progress, summary, createdTranscript, onSessionCreated]);

  const handleFormSubmit = async (formData) => {
    setSubmitting(true);
    setSubmitError(null);

    try {
      // Create the transcript
      const response = await apiFetch('/api/google/meet/transcripts/manual', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create transcript');
      }

      // Store created transcript info
      setCreatedTranscript(data.transcript);

      // Move to analyzing step
      setStep('analyzing');

      // Start the SSE analysis
      startAnalysis(data.transcript.id);

    } catch (err) {
      console.error('Error creating transcript:', err);
      setSubmitError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = useCallback(() => {
    if (isAnalyzing) {
      // Analysis is running - it will continue in background
      // The SSE hook will keep the connection open
    }

    // Reset drawer state
    setStep('form');
    setSubmitError(null);
    setCreatedTranscript(null);
    resetAnalysis();
    onClose();
  }, [isAnalyzing, resetAnalysis, onClose]);

  const handleRetryAnalysis = useCallback(() => {
    if (createdTranscript) {
      resetAnalysis();
      setStep('analyzing');
      startAnalysis(createdTranscript.id);
    }
  }, [createdTranscript, resetAnalysis, startAnalysis]);

  const handleViewSession = useCallback(() => {
    handleClose();
  }, [handleClose]);

  // Don't render if not open
  if (!isOpen) {
    return null;
  }

  return (
    <>
      {/* Backdrop */}
      <div className="drawer-backdrop" onClick={handleClose} />

      {/* Drawer */}
      <div className={`add-session-drawer ${isOpen ? 'open' : ''}`}>
        <div className="drawer-header">
          <h2>
            {step === 'form' && 'Add New Session'}
            {step === 'analyzing' && 'Analyzing Session'}
            {step === 'complete' && 'Session Created'}
          </h2>
          <button className="drawer-close" onClick={handleClose} aria-label="Close">
            &#x2715;
          </button>
        </div>

        <div className="drawer-content">
          {step === 'form' && (
            <>
              {submitError && (
                <div className="drawer-error">
                  <span className="error-icon">!</span>
                  {submitError}
                </div>
              )}

              {loadingStudents ? (
                <div className="loading-students">Loading students...</div>
              ) : (
                <SimplifiedTranscriptForm
                  students={students}
                  initialStudentUuid={selectedStudentUuid}
                  onSubmit={handleFormSubmit}
                  onCancel={handleClose}
                  disabled={submitting}
                />
              )}
            </>
          )}

          {step === 'analyzing' && (
            <div className="analyzing-container">
              <div className="analyzing-header">
                <p>Your session is being analyzed by our AI. This usually takes 10-30 seconds.</p>
              </div>

              <AnalysisProgress
                progress={progress}
                error={analysisError}
                onRetry={handleRetryAnalysis}
                phases={phases}
              />

              {isAnalyzing && (
                <p className="analyzing-tip">
                  You can close this drawer - analysis will continue in the background.
                </p>
              )}
            </div>
          )}

          {step === 'complete' && (
            <div className="complete-container">
              <div className="complete-icon">&#x2713;</div>
              <h3>Session Created Successfully!</h3>
              <p>Your session has been analyzed and is now available in the timeline.</p>

              {summary && (
                <div className="summary-preview">
                  <h4>Session Overview</h4>
                  <p>{summary.session_overview || 'Analysis complete.'}</p>

                  {summary.topics_covered && summary.topics_covered.length > 0 && (
                    <>
                      <h4>Topics Covered</h4>
                      <ul>
                        {summary.topics_covered.slice(0, 3).map((topic, i) => (
                          <li key={i}>{topic}</li>
                        ))}
                        {summary.topics_covered.length > 3 && (
                          <li className="more-items">+{summary.topics_covered.length - 3} more</li>
                        )}
                      </ul>
                    </>
                  )}
                </div>
              )}

              <div className="complete-actions">
                <button className="btn btn-primary" onClick={handleViewSession}>
                  View Session
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .drawer-backdrop {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          z-index: 999;
          animation: fadeIn 0.2s ease;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .add-session-drawer {
          position: fixed;
          right: 0;
          top: 0;
          height: 100vh;
          width: 480px;
          max-width: 90vw;
          background: var(--bg-secondary, #1e1e2e);
          box-shadow: -4px 0 24px rgba(0, 0, 0, 0.3);
          z-index: 1000;
          display: flex;
          flex-direction: column;
          transform: translateX(100%);
          animation: slideIn 0.3s ease forwards;
        }

        @keyframes slideIn {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }

        .drawer-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 24px;
          border-bottom: 1px solid var(--border-color, #3a3a4e);
        }

        .drawer-header h2 {
          margin: 0;
          font-size: 20px;
          font-weight: 600;
          color: var(--text-color, #fff);
        }

        .drawer-close {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          border: none;
          background: transparent;
          color: var(--text-muted, #888);
          font-size: 18px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
        }

        .drawer-close:hover {
          background: var(--bg-tertiary, #2a2a3e);
          color: var(--text-color, #fff);
        }

        .drawer-content {
          flex: 1;
          overflow-y: auto;
          padding: 24px;
        }

        .drawer-error {
          background: rgba(244, 67, 54, 0.1);
          border: 1px solid var(--error-color, #f44336);
          color: var(--error-color, #f44336);
          padding: 12px 16px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 16px;
        }

        .drawer-error .error-icon {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: var(--error-color, #f44336);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          font-weight: bold;
        }

        .loading-students {
          text-align: center;
          padding: 40px;
          color: var(--text-muted, #888);
        }

        .analyzing-container {
          padding: 20px 0;
        }

        .analyzing-header p {
          color: var(--text-muted, #888);
          margin: 0 0 20px 0;
        }

        .analyzing-tip {
          color: var(--text-muted, #888);
          font-size: 13px;
          text-align: center;
          margin-top: 16px;
        }

        .complete-container {
          text-align: center;
          padding: 40px 0;
        }

        .complete-icon {
          width: 64px;
          height: 64px;
          border-radius: 50%;
          background: var(--success-color, #4caf50);
          color: white;
          font-size: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 20px;
        }

        .complete-container h3 {
          margin: 0 0 8px 0;
          color: var(--text-color, #fff);
        }

        .complete-container > p {
          color: var(--text-muted, #888);
          margin: 0 0 24px 0;
        }

        .summary-preview {
          background: var(--card-bg, #2a2a3e);
          border-radius: 12px;
          padding: 20px;
          text-align: left;
          margin-bottom: 24px;
        }

        .summary-preview h4 {
          font-size: 12px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: var(--text-muted, #888);
          margin: 0 0 8px 0;
        }

        .summary-preview h4:not(:first-child) {
          margin-top: 16px;
        }

        .summary-preview p {
          color: var(--text-color, #fff);
          margin: 0;
          line-height: 1.5;
        }

        .summary-preview ul {
          margin: 0;
          padding-left: 20px;
          color: var(--text-color, #fff);
        }

        .summary-preview li {
          margin-bottom: 4px;
        }

        .summary-preview .more-items {
          color: var(--text-muted, #888);
          font-style: italic;
        }

        .complete-actions {
          display: flex;
          justify-content: center;
        }

        .complete-actions .btn {
          padding: 12px 32px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          border: none;
        }

        .complete-actions .btn-primary {
          background: var(--primary-color, #6366f1);
          color: white;
        }

        .complete-actions .btn-primary:hover {
          background: var(--primary-dark, #4f46e5);
        }
      `}</style>
    </>
  );
};

export default AddSessionDrawer;
